import React from 'react';
import { FieldError } from 'react-hook-form';
import type { GestureResponderEvent } from 'react-native';
import { useLingui } from '@lingui/react/macro';

import { BoxProps, Paragraph, Text } from '@rover/kibble/core';
import A11yHidden from '@rover/kibble/styles/A11yHidden';
import type { ThemeTextSizes } from '@rover/kibble/styles/themeTypes';

import translateFieldError from '../core/translateFieldError';

export type RequirednessLabel = 'auto' | 'show' | 'hide';

export type SharedTypographyProps = {
  /** The field's label: the question it asks, or the thing it names. */
  primaryLabel: string;
  /** Text size of the label, to match the surrounding hierarchy. */
  primaryLabelSize?: '100' | '200' | '300';
  /** One line under the field that applies to all of it. */
  helperMessage?: string | React.ComponentType<{ helperMessageId: string }>;
  /** Supporting context under the label. */
  secondaryLabel?: string;
  /** Disables the whole field. */
  disabled?: boolean;
  required?: boolean;
  /** Hides the label visually. Screen readers still read it, so the field keeps its name. */
  primaryLabelA11yHidden?: boolean;
  /**
   * Whether the field's visible label states that it is required or optional.
   * Requiredness always reaches assistive technology, whatever this is set to.
   *
   * `auto` is the default and covers the two common cases:
   * - required field: appends `(required)`.
   * - optional field: appends nothing, leaving optionality to be read from context.
   *
   * Opt in where a field needs the other treatment:
   * - `show` on an optional field appends `(optional)`, for when leaving the field
   *   blank would otherwise look like an oversight.
   * - `hide` on a required field appends nothing, for inputs people don't read as
   *   form fields: a single-field form, or a text input inside a larger control.
   *
   * The two remaining combinations render exactly what `auto` renders:
   * - `show` on a required field already appends `(required)`.
   * - `hide` on an optional field already appends nothing.
   */
  requirednessLabel?: RequirednessLabel;
};

export type PrimaryLabelVariants =
  | 'default'
  | 'option-label'
  | 'legalConsent'
  | 'order-option'
  | 'compact';

export interface PrimaryLabelProps {
  primaryLabel: string;
  primaryLabelSize?: '100' | '200' | '300';
  required: boolean;
  id?: string;
  webInputId?: string;
  primaryLabelA11yHidden?: boolean;
  accessibilityLabel?: string;
  sx?: BoxProps['sx'];
  as?: 'label' | 'legend';
  // MouseEvent on web, GestureResponderEvent on native
  // to do this right we need to split files, I'll do it later
  onClick?: React.MouseEventHandler<HTMLElement> | GestureResponderEvent;
  variant?: PrimaryLabelVariants;
  primaryLabelVariant?: PrimaryLabelVariants;
  requirednessLabel?: RequirednessLabel;
}

type PrimaryLabelInternalTextProps = {
  // Platform-specific props (will be separated into webProps/rxnProps)
  id?: string;
  testID?: string;
  as?: 'label' | 'legend'; // Keep input types strict - only what PrimaryLabel accepts
  htmlFor?: string;
  'data-testid'?: string;
  // Common props (passed directly to component)
  fontWeight?: 'semibold' | 'regular';
  textColor?: 'primary' | 'secondary' | 'tertiary';
  size?: ThemeTextSizes;
  height?: number;
};

export const PrimaryLabel = ({
  primaryLabel,
  accessibilityLabel,
  required,
  id,
  webInputId,
  primaryLabelA11yHidden,
  sx,
  as = 'label',
  onClick,
  variant = 'default',
  primaryLabelSize,
  requirednessLabel = 'auto',
}: PrimaryLabelProps): JSX.Element => {
  const { t } = useLingui();

  let Component: typeof Text | typeof Paragraph = Text;

  // Determine component type
  if (variant === 'option-label' || variant === 'legalConsent') {
    Component = Paragraph;
  }

  const textProps: PrimaryLabelInternalTextProps = process.env.JS_ENV_NATIVE
    ? {
        testID: 'kff-primary-label',
      }
    : {
        as, // Keep original 'as' value in textProps
        id,
        htmlFor: webInputId,
        'data-testid': 'kff-primary-label',
      };

  if (variant === 'option-label' || variant === 'legalConsent') {
    textProps.fontWeight = 'regular';
  }

  if (variant === 'default') {
    textProps.fontWeight = 'semibold';
  }

  if (variant === 'legalConsent') {
    textProps.textColor = 'tertiary';
    textProps.size = '100';
  }

  if (variant === 'order-option') {
    textProps.fontWeight = 'semibold';
    textProps.size = '100';
  }

  if (variant === 'compact') {
    textProps.fontWeight = 'semibold';
    textProps.size = '100';
    textProps.textColor = 'secondary';
  }

  // primaryLabelSize overrides variant styles
  if (primaryLabelSize) {
    textProps.size = primaryLabelSize;
  }

  let requirednessSuffix: string | undefined;
  if (required && requirednessLabel !== 'hide') {
    requirednessSuffix = t({
      message: `(required)`,
      comment:
        'Follows a form field label to mark the field as required. Keep the parentheses, or the convention your language uses for them.',
    });
  } else if (!required && requirednessLabel === 'show') {
    requirednessSuffix = t({
      message: `(optional)`,
      comment:
        'Follows a form field label to mark the field as optional. Keep the parentheses, or the convention your language uses for them.',
    });
  }

  let finalSx = sx;

  if (primaryLabelA11yHidden && process.env.JS_ENV_NATIVE) {
    // There isn't clear documentation on how to a11y hide a label in native
    // removing the label entirely causes issues with RadioField and CheckboxField,
    // instead we'll try setting the height&mb to 0 so the element is present but invisible
    textProps.height = 0;
    finalSx = { ...sx, mb: 0 };
  } else if (primaryLabelA11yHidden) {
    finalSx = { A11yHidden, ...sx };
  }

  const {
    as: textPropsAs,
    id: textPropsId,
    htmlFor: textPropsHtmlFor,
    'data-testid': textPropsDataTestId,
    testID: textPropsTestID,
    ...otherTextProps
  } = textProps;

  // Build webProps based on component type to ensure type compatibility
  let webProps;
  if (Component === Text) {
    webProps = {
      as: textPropsAs as 'span' | 'div' | 'label' | 'legend' | 'caption' | 'strong',
      id: textPropsId,
      htmlFor: textPropsHtmlFor,
      'data-testid': textPropsDataTestId,
    };
  } else {
    // For Paragraph, keep label/legend as-is now that Paragraph supports them
    const paragraphAs = textPropsAs || 'p';
    webProps = {
      as: paragraphAs,
      id: textPropsId,
      htmlFor: textPropsHtmlFor, // Now supported since Paragraph can be a label!
      'data-testid': textPropsDataTestId,
    };
  }

  const rxnProps = {
    testID: textPropsTestID,
    accessibilityLabel,
  };

  return (
    <Component
      {...otherTextProps}
      webProps={webProps}
      rxnProps={rxnProps}
      sx={finalSx}
      mb="1x"
      // This actually works just fine, but getting TS to be happy is a pain
      // I'll make this better later
      // @ts-expect-error
      onClick={onClick}
    >
      {primaryLabel}
      {requirednessSuffix ? (
        <Text
          fontWeight="regular"
          textColor={textProps.textColor ?? 'secondary'}
          size={textProps.size}
        >
          {' '}
          {requirednessSuffix}
        </Text>
      ) : null}
    </Component>
  );
};
export interface SecondaryLabelProps {
  secondaryLabel?: string;
  labelId?: string;
  mb?: BoxProps['mb'];
}

export const SecondaryLabel = ({
  secondaryLabel,
  labelId,
  mb = '1x',
}: SecondaryLabelProps): JSX.Element | null => {
  if (!secondaryLabel) return null;

  const props = process.env.JS_ENV_NATIVE
    ? {
        testID: 'kff-secondary-label',
      }
    : ({
        id: labelId,
        'data-testid': 'kff-secondary-label',
        as: 'span',
      } as const);

  return (
    <Paragraph textColor="tertiary" mb={mb} size="100" {...props}>
      {secondaryLabel}
    </Paragraph>
  );
};

export interface FeedbackMessageProps {
  fieldError?: FieldError;
  fieldErrorId?: string;
  helperMessage?: string | React.ComponentType<{ helperMessageId: string }>;
  helperMessageId?: string;
  translatedFieldErrorMessage?: string;
}

export const FeedbackMessageCommonStyles = {
  as: 'span',
  mb: '0x',
  mt: '1x',
  size: '100',
} as const;

interface CustomHelperMessageWrapperProps {
  helperMessageId?: string;
  children: React.ReactNode;
}

export const CustomHelperMessageWrapper: React.FC<CustomHelperMessageWrapperProps> = ({
  helperMessageId,
  children,
}) => {
  const platformSpecificProps = process.env.JS_ENV_NATIVE
    ? {}
    : {
        id: helperMessageId,
        'data-testid': 'kff-help-msg',
      };

  return (
    <Paragraph {...FeedbackMessageCommonStyles} textColor="tertiary" {...platformSpecificProps}>
      {children}
    </Paragraph>
  );
};

export const FeedbackMessage = ({
  fieldError,
  fieldErrorId,
  helperMessage,
  helperMessageId,
  translatedFieldErrorMessage,
}: FeedbackMessageProps): JSX.Element | null => {
  const { i18n } = useLingui();
  let errorMessageComponent: JSX.Element | null = null;
  let helperMessageComponent: JSX.Element | null = null;

  let errorMessageText: string | undefined;
  if (translatedFieldErrorMessage) {
    errorMessageText = translatedFieldErrorMessage;
  } else if (fieldError) {
    errorMessageText = translateFieldError(fieldError, i18n);
  }

  if (errorMessageText) {
    const platformSpecificProps = process.env.JS_ENV_NATIVE
      ? {}
      : {
          id: fieldErrorId,
        };
    errorMessageComponent = (
      <Paragraph
        {...FeedbackMessageCommonStyles}
        {...platformSpecificProps}
        textColor="error"
        role="alert"
      >
        {errorMessageText}
      </Paragraph>
    );
  }

  if (helperMessage) {
    if (typeof helperMessage === 'function') {
      helperMessageComponent = React.createElement(
        helperMessage,
        helperMessageId ? { helperMessageId } : undefined
      );
    } else {
      const platformSpecificProps = process.env.JS_ENV_NATIVE
        ? {}
        : { id: helperMessageId, 'data-testid': 'kff-help-msg' };
      helperMessageComponent = (
        <Paragraph {...FeedbackMessageCommonStyles} {...platformSpecificProps} textColor="tertiary">
          {helperMessage}
        </Paragraph>
      );
    }
  }

  return (
    <>
      {helperMessageComponent}
      {errorMessageComponent}
    </>
  );
};
